import { Component, inject, Input, Pipe, PipeTransform } from '@angular/core';
import { NgClass } from '@angular/common';
import { NzImageService } from 'ng-zorro-antd/image';
import { FlIconComponent } from '@common';
import { MessageExerciseDetail } from '../../message.api.service';
import { ExerciseDetailResultEnum } from '@shared/model/exercise.models';

/**
 * 分数标签配色 Pipe：按作答结果映射红/橙/绿三色（背景/描边/文字）
 *
 * 错误(3)红、半对(2)橙、正确(1)/未阅(0)绿。分数只展示数值，颜色由 result 决定。
 */
@Pipe({ name: 'scoreTagClass', standalone: true })
export class ScoreTagClassPipe implements PipeTransform {
  transform(result: number | undefined): string {
    switch (result) {
      case ExerciseDetailResultEnum.WRONG:
        return 'border-red-r5 bg-red-r6 text-red-r1';
      case ExerciseDetailResultEnum.HALF_RIGHT:
        return 'border-orange-o5 bg-orange-o6 text-orange-o1';
      default:
        return 'border-green-g5 bg-green-g6 text-green-g1';
    }
  }
}

/**
 * 单小题学生作答详情（题目消息姓名栏内的一个作答块）
 *
 * 按题目类型渲染：
 * - 单选/多选：nz-tag 显示选项，答题正确(蓝) / 错误(红)
 * - 主观题：跳过(100)显示灰色「跳过」；未阅(0)显示灰色「未阅」待批标签；已批则有照片 → 分数 tag + 图片图标（点击弹原图），无照片 → 仅分数 tag
 */
@Component({
  selector: 'fl-message-exercise-detail',
  template: `
    @if (detail.questionType === 0 || detail.questionType === 1) {
      <!-- 单选/多选：有选项时按答题结果绿/红展示选项；未作答(answer 空)则白底灰边占位 -->
      @if (detail.answer) {
        <span
          class="inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-[4px] border px-[4px] text-[12px] leading-none"
          [ngClass]="detail.result | scoreTagClass"
          >{{ detail.answer }}</span
        >
      } @else {
        <!-- 未答：描边药丸中性灰底填充(n9)、描边(n5)、文字(n2) -->
        <span
          class="inline-flex h-[16px] items-center justify-center rounded-[4px] border border-neutral-n5 bg-neutral-n9 px-[4px] text-[12px] text-neutral-n2"
          >未答</span
        >
      }
    } @else {
      <!-- 主观题：跳过显示灰色标签；未阅(0)显示灰色待批标签，已批才按分数着色 -->
      @if (detail.result === paperExerciseDetailResultEnum.SKIP) {
        <!-- 跳过：学生未作答，不参与批改 -->
        <span
          class="inline-flex h-[16px] items-center justify-center rounded-[4px] border border-neutral-n5 bg-neutral-n9 px-[4px] text-[12px] text-neutral-n2"
          >跳过</span
        >
      } @else if (detail.result === paperExerciseDetailResultEnum.UNMARK) {
        <span
          class="inline-flex h-[16px] items-center justify-center rounded-[4px] border border-neutral-n5 bg-neutral-n9 px-[4px] text-[12px] text-neutral-n2"
          [class.cursor-pointer]="detail.photos?.length"
          title="待批改，点击查看作答"
          (click)="detail.photos?.length && onPreviewPhoto()"
        >
          未阅
          @if (detail.photos?.length) {
            <lib-fl-icon name="icon-yuantu" size="12"></lib-fl-icon>
          }
        </span>
      } @else if (detail.photos?.length) {
        <!-- 主观题有照片：分数 + 查看图标合并为一颗药丸（点击弹原图） -->
        <span
          class="inline-flex h-[16px] items-center justify-center gap-[4px] rounded-[4px] border px-[4px] text-[12px] cursor-pointer transition-colors"
          [ngClass]="detail.result | scoreTagClass"
          title="查看作答"
          (click)="onPreviewPhoto()"
        >
          {{ detail.score }}分
          <lib-fl-icon name="icon-yuantu" size="12"></lib-fl-icon>
        </span>
      } @else {
        <!-- 主观题无照片：仅分数标签 -->
        <span
          class="inline-flex h-[16px] items-center justify-center rounded-[4px] border px-[4px] text-[12px]"
          [ngClass]="detail.result | scoreTagClass"
          >{{ detail.score }}分</span
        >
      }
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
    `,
  ],
  standalone: true,
  imports: [NgClass, FlIconComponent, ScoreTagClassPipe],
})
export class MessageExerciseDetailComponent {
  /** 暴露给模板判定的结果枚举（未阅=UNMARK） */
  paperExerciseDetailResultEnum = ExerciseDetailResultEnum;

  private imageSrv = inject(NzImageService);

  /** 单个小题作答详情 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() detail!: MessageExerciseDetail;

  /** 点击图片图标：弹出作答照片预览（多张可切换） */
  onPreviewPhoto() {
    const photos = this.detail?.photos ?? [];
    if (photos.length) {
      this.imageSrv.preview(photos.map(src => ({ src })));
    }
  }
}
